// Reports the JavaScript errors of the browsers to the API (POST /js-report, App\Controller\JsReportController),
// the way CSP violations already are: window "error" and "unhandledrejection" events, Vue errors (vue:error
// hook) and a script or stylesheet that fails to load. Each distinct error is sent once per page load, five
// at most, with sendBeacon so a page being left still reports. Nothing here can throw back into the page.
//
// Why: on 16/10/2026 every modal of the site threw at mount for 7 hours in production and no server-side
// signal moved (5xx, prod.log, PM2). The Nagios probe deploy/ops/check_js_errors.py counts these reports.
export default defineNuxtPlugin({
  name: 'error-report',
  enforce: 'pre',
  setup(nuxtApp) {
    const config = useRuntimeConfig()
    const wsUrl = config.public.wsUrl as string
    if (!wsUrl || typeof window === 'undefined') return
    const endpoint = `${wsUrl.replace(/\/$/, '')}/js-report`
    const build = (config.app as { buildId?: string }).buildId ?? ''
    const seen = new Set<string>()
    let sent = 0

    const report = (
      kind: string,
      message: unknown,
      stack?: unknown,
      source?: string,
    ) => {
      try {
        const text = String(message ?? '').slice(0, 500)
        if (text === '' || sent >= 5) return
        const key = `${kind}|${text}`
        if (seen.has(key)) return
        seen.add(key)
        sent++
        const body = JSON.stringify({
          kind,
          message: text,
          stack: typeof stack === 'string' ? stack.slice(0, 2000) : '',
          source: (source ?? '').slice(0, 300),
          page: window.location.pathname,
          build,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
        })
        // text/plain: a "simple" request, no CORS preflight when the API is on another origin (the
        // controller decodes the raw body whatever its type)
        const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' })
        if (!navigator.sendBeacon || !navigator.sendBeacon(endpoint, blob)) {
          fetch(endpoint, {
            method: 'POST',
            body,
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
            keepalive: true,
          }).catch(() => {})
        }
      } catch {
        // a report must never break the page
      }
    }

    window.addEventListener(
      'error',
      (event) => {
        const target = event.target as Element | null
        if (target && target !== (window as unknown as Element) && 'tagName' in target) {
          // a resource that failed to load: only our own scripts and stylesheets matter (a stale /_nuxt/
          // chunk after a deploy). A missing image is not an application error, and a third-party script
          // (GTM, Facebook, Twitter) failing is the visitor's ad blocker: the first minutes in production
          // showed nothing else
          const tag = target.tagName
          if (tag === 'SCRIPT' || tag === 'LINK') {
            const url =
              (target as HTMLScriptElement).src ||
              (target as HTMLLinkElement).href ||
              ''
            if (url.startsWith(window.location.origin + '/')) {
              report('resource', `${tag} failed: ${url}`)
            }
          }
          return
        }
        report(
          'error',
          event.message,
          event.error?.stack,
          event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : '',
        )
      },
      true,
    )
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason as { message?: unknown; stack?: unknown } | null
      report(
        'unhandledrejection',
        reason?.message ?? reason,
        reason?.stack,
      )
    })
    nuxtApp.hook('vue:error', (error, _instance, info) => {
      const err = error as { message?: unknown; stack?: unknown } | null
      report('vue', err?.message ?? error, err?.stack, String(info ?? ''))
    })
  },
})
